import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositePublish.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { AlertCircle, RefreshCw } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeBuildStatusBanner from '../components/MicrositeBuildStatusBanner/MicrositeBuildStatusBanner'
import MicrositePublishButton from '../components/MicrositePublishButton/MicrositePublishButton'
import MicrositePrePublishChecklist from '../components/MicrositePrePublishChecklist/MicrositePrePublishChecklist'
import MicrositeBuildLog from '../components/MicrositeBuildLog/MicrositeBuildLog'
import MicrositeCopyableUrl from '../components/MicrositeCopyableUrl/MicrositeCopyableUrl'

const POLL_INTERVAL_MS = 4000

/**
 * MicrositePublish
 * Action screen for publishing the microsite — shows current build
 * status, a pre-publish readiness checklist, and the publish CTA.
 * Polls micrositeGetBuildStatus while a build is in progress so the
 * status banner updates without a manual refresh.
 */
const MicrositePublish = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [buildStatus, setBuildStatus] = useState(null)
    const [domain, setDomain] = useState(null)
    const [publishing, setPublishing] = useState(false)

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadStatus = useCallback(async (showSpinner) => {
        if (showSpinner) setLoading(true)
        const res = await post('micrositeGetBuildStatus')
        if (res?.status === 'success') {
            setBuildStatus(res.response?.buildStatus || {})
            setDomain(res.response?.primaryDomain || null)
        } else if (showSpinner) {
            showToast('Could not load publish status. Please refresh.', 'error')
        }
        if (showSpinner) setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadStatus(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    // Poll while a build is in progress
    useEffect(() => {
        if (buildStatus?.buildStatus !== 'BUILDING') return
        const interval = setInterval(() => loadStatus(false), POLL_INTERVAL_MS)
        return () => clearInterval(interval)
    }, [buildStatus?.buildStatus, loadStatus])

    const handlePublish = async () => {
        setPublishing(true)
        const res = await post('micrositePublishSite')
        setPublishing(false)

        if (res?.status === 'success') {
            showToast('Publish started — this may take a minute')
            setBuildStatus((prev) => ({ ...prev, buildStatus: 'BUILDING' }))
            loadStatus(false)
        } else {
            showToast('Could not start publish. Please try again.', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading publish status…</span>
            </div>
        )
    }

    const checklistItems = [
        { label: 'Primary domain verified', ok: !!domain?.dnsVerified },
        { label: 'SSL certificate active', ok: domain?.sslStatus === 'ACTIVE' },
        { label: 'Hero section content filled in', ok: !!buildStatus?.heroSectionReady }
    ]

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    <AlertCircle size={14} />
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Publish"
                subtitle="Push your changes live for visitors to see."
            />

            <MicrositeBuildStatusBanner status={buildStatus?.buildStatus || 'PENDING'} />

            {domain?.domainName && (
                <section className={styles.fieldGroup}>
                    <h2 className={styles.groupTitle}>Your site</h2>
                    <MicrositeCopyableUrl url={`https://${domain.domainName}`} />
                </section>
            )}

            <section className={styles.fieldGroup}>
                <MicrositePrePublishChecklist items={checklistItems} />
            </section>

            <div className={styles.publishRow}>
                <MicrositePublishButton buildStatus={buildStatus?.buildStatus} onClick={handlePublish} />
            </div>

            <MicrositeBuildLog
                log={buildStatus?.buildLog}
                defaultOpen={buildStatus?.buildStatus === 'FAILED'}
            />
        </div>
    )
}

export default MicrositePublish